import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Grid, 
    Card, 
    CardMedia, 
    CardContent, 
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import { H2, H5 } from 'components/CustomTypography';

const ModelTryOnSelector = ({ 
    open, 
    onClose, 
    onSelectModel, 
    models, 
    loading,
    generating 
}) => {
    const [selectedModel, setSelectedModel] = useState(null);

    const handleSelectModel = (model) => {
        setSelectedModel(model._id);
    };

    const handleConfirm = () => {
        if (selectedModel && onSelectModel) {
            onSelectModel(selectedModel);
        }
    };

    const handleAutoSelect = () => {
        if (onSelectModel) {
            onSelectModel(null); // Pass null to trigger auto-selection
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Generate Virtual Try-On with Model
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Select a specific model or use auto-selection to let AI choose the best model based on product gender. The result will be added as the first image of your product.
                        </Alert>
                        <Grid container spacing={2}>
                            {models && models.map((model) => (
                                <Grid item xs={6} sm={4} key={model._id}>
                                    <Card 
                                        onClick={() => handleSelectModel(model)}
                                        sx={{
                                            cursor: 'pointer',
                                            border: selectedModel?._id === model._id ? '3px solid' : '2px solid',
                                            borderColor: selectedModel?._id === model._id ? 'primary.main' : 'grey.300',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: 'primary.light',
                                                transform: 'scale(1.02)',
                                            }
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={model.image?.cdn?.secure_url || model.image?.full_size_cdn?.secure_url}
                                            alt={model.name}
                                            sx={{ objectFit: 'cover' }}
                                        />
                                        <CardContent>
                                            <Typography variant="body2" fontWeight="bold">
                                                {model.name}
                                            </Typography>
                                            {model.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {model.description}
                                                </Typography>
                                            )}
                                            {model.gender && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {model.gender}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        {(!models || models.length === 0) && !loading && (
                            <Box textAlign="center" p={4}>
                                <Typography color="text.secondary">
                                    No models available
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={generating}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleAutoSelect} 
                    variant="outlined" 
                    color="primary"
                    disabled={generating}
                    sx={{ mr: 1 }}
                >
                    {generating ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Generating...
                        </>
                    ) : (
                        'Auto-Select Model'
                    )}
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    variant="contained" 
                    color="primary"
                    disabled={!selectedModel || generating}
                >
                    {generating ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Generating...
                        </>
                    ) : (
                        'Generate with Selected'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModelTryOnSelector;
